import React, { useState } from "react";
import axiosInstance from "../axiosInstance";

const fmt = (v) => '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN');
const fmtDate = (v) => {
    if (!v) return '--';
    const s = v.toString().substring(0, 10);
    const [y, m, d] = s.split('-');
    return `${d}-${m}-${y}`;
};

export default function AccountsCustomerLedger() {
    const [query,    setQuery]    = useState('');
    const [results,  setResults]  = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);
    const [ledger,   setLedger]   = useState([]);
    const [loading,  setLoading]  = useState(false);

    const search = async () => {
        if (!query.trim()) return;
        setSearching(true);
        setSelected(null);
        setLedger([]);
        try {
            const res = await axiosInstance.get(`/finance/reg/${encodeURIComponent(query.trim())}`);
            const data = res.data;
            setResults(Array.isArray(data) ? data : (data ? [data] : []));
        } catch (e) { console.error(e); setResults([]); }
        finally { setSearching(false); }
    };

    const openLedger = async (loan) => {
        setSelected(loan);
        setLedger([]);
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/duereceipt/${loan.id}`);
            const data = res.data;
            setLedger(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const loanAmt    = parseFloat(selected?.loan_amount)  || 0;
    const dueBalance = parseFloat(selected?.due_balance)  || 0;
    const fineBalance= parseFloat(selected?.fine_balance) || 0;
    const collected  = ledger.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);

    const exportCSV = () => {
        const headers = 'Date,Type,Due No,Amount,Payment,Running Balance';
        const data = ledger.map(r => [fmtDate(r.createdAt), r.type || 'Receipt', r.due_no, r.total, r.payment_type, r.running_balance].map(v => `"${v ?? ''}"`).join(','));
        const blob = new Blob([[headers, ...data].join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `Ledger-${selected?.reg_no}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">
                                <i className="fas fa-search text-info mr-2"></i>Customer Ledger
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/accounts">Accounts</a></li>
                                <li className="breadcrumb-item active">Customer Ledger</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    {/* Search bar */}
                    <div className="card mb-3">
                        <div className="card-body py-2">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by Reg No, Name, or Mobile..."
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && search()}
                                />
                                <div className="input-group-append">
                                    <button className="btn btn-info" onClick={search} disabled={searching}>
                                        {searching ? <span className="spinner-border spinner-border-sm"></span> : <i className="fas fa-search"></i>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search results */}
                    {results.length > 0 && !selected && (
                        <div className="card mb-3">
                            <div className="card-header"><h3 className="card-title">Search Results ({results.length})</h3></div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="thead-light">
                                            <tr><th>Reg No</th><th>Borrower</th><th>Mobile</th><th>Loan Amt</th><th>Due Bal</th><th>Fine Bal</th><th></th></tr>
                                        </thead>
                                        <tbody>
                                            {results.map((l, i) => (
                                                <tr key={i}>
                                                    <td>{l.reg_no || '--'}</td>
                                                    <td>{l.borrower_name || '--'}</td>
                                                    <td>{l.borrower_mobile || '--'}</td>
                                                    <td>{fmt(l.loan_amount)}</td>
                                                    <td className="text-warning">{fmt(l.due_balance)}</td>
                                                    <td className="text-danger">{fmt(l.fine_balance)}</td>
                                                    <td>
                                                        <button className="btn btn-sm btn-outline-info" onClick={() => openLedger(l)}>
                                                            <i className="fas fa-book-open mr-1"></i>Ledger
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Ledger */}
                    {selected && (
                        <>
                            <div className="row mb-2 align-items-center">
                                <div className="col-md-8">
                                    <div className="callout callout-info py-2">
                                        <span className="mr-3 font-weight-bold">{selected.reg_no}</span>
                                        <span className="mr-3">{selected.borrower_name}</span>
                                        <span className="mr-3">Loan: <strong>{fmt(loanAmt)}</strong></span>
                                        <span className="mr-3 text-warning">Due: <strong>{fmt(dueBalance)}</strong></span>
                                        <span className="text-danger">Fine: <strong>{fmt(fineBalance)}</strong></span>
                                    </div>
                                </div>
                                <div className="col-md-4 text-right">
                                    <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => { setSelected(null); }}>
                                        <i className="fas fa-arrow-left mr-1"></i>Back
                                    </button>
                                    <button className="btn btn-sm btn-outline-success" onClick={exportCSV} disabled={ledger.length === 0}>
                                        <i className="fas fa-file-csv mr-1"></i>CSV
                                    </button>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-4 mb-2">
                                    <div className="callout callout-success py-2">
                                        <span className="text-muted">Total Collected</span>
                                        <p className="h4 font-weight-bold text-success mb-0">{fmt(collected)}</p>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-2">
                                    <div className="callout callout-warning py-2">
                                        <span className="text-muted">Still Outstanding</span>
                                        <p className="h4 font-weight-bold text-warning mb-0">{fmt(loanAmt - collected)}</p>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-2">
                                    <div className="callout callout-info py-2">
                                        <span className="text-muted">EMI / Receipts</span>
                                        <p className="h4 font-weight-bold mb-0">{ledger.length}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-body p-0">
                                    {loading ? (
                                        <div className="text-center py-5"><div className="spinner-border text-info" role="status"></div></div>
                                    ) : ledger.length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>No payment history found
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-hover table-sm mb-0">
                                                <thead className="thead-light">
                                                    <tr><th>#</th><th>Date</th><th>Due No</th><th>Amount</th><th>Payment</th></tr>
                                                </thead>
                                                <tbody>
                                                    {ledger.map((r, i) => (
                                                        <tr key={i}>
                                                            <td>{i + 1}</td>
                                                            <td>{fmtDate(r.createdAt)}</td>
                                                            <td>{r.due_no || '--'}</td>
                                                            <td className="font-weight-bold text-success">{fmt(r.total)}</td>
                                                            <td>{r.payment_type || '--'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
